import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { Triangle, Square, Circle, Info } from 'lucide-react';

const ABOUT_TEXT = `Open Structures is not just an interactive experience — it is a meditation on becoming, a digital embodiment of how knowledge, uncertainty, and imagination co‑evolve.

In the world of Open Structures, every placed piece is more than a shape — it is a hypothesis, an assertion against the vast field of the Unknown. Each form you draw and each structure you complete is a momentary crystallization of understanding within a space that refuses final definition.

This space, the Unknown, is not chaos nor emptiness. It is the permission field, the ontological ground of possibility itself — the underlying condition that makes emergence, creativity, and even laws of order possible. No amount of measurement, pattern, or certainty ever fully contains it, because the act of knowing itself expands it. As knowledge grows, so too does what remains unknown, a horizon that forever shifts just beyond our grasp.

In Open Structures, players move on the boundary between these domains:

— The Known: the shapes you place, the patterns you discover
— The Not‑Yet‑Known: the anticipations and emergent confidences
— The Unknown: the fertile field of potential that resists capture yet sustains all possibility

Every act of building is not an attempt to conquer this field, but a moment of participation with it. Structures harden into equilibrium, local islands of certainty, but when they become too rigid, they begin to break down. Certainty that refuses to yield loses contact with the world of potential and is dissolved by the very Unknown it sought to contain.

Thus the cycle of Open Structures becomes a metaphor for the evolution of understanding itself:

— Creation: placing new pieces and forming hypotheses
— Equilibrium: reaching a coherent structure, a local "known"
— Disintegration: when certainty becomes too rigid, it collapses
— Re‑emergence: the cycle renews and the Unknown beckons

This is not merely a game mechanic, but a philosophical movement between state and flux, between certainty and openness.

Like human theories throughout history, each explanatory structure stands until something unfolds that lies outside its frame, demanding reconfiguration. Classical models give way to new paradigms; certainty becomes provisional; what we once took for truth becomes the seedbed of new mysteries. The journey through Open Structures mirrors this evolution of knowledge itself: a continual oscillation between what we define and what forever eludes final definition — the eternal field of becoming.`;

export default function AuthPage() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showAbout, setShowAbout] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    const fd = new FormData(e.target);
    try {
      await login(fd.get('username'), fd.get('password'));
      navigate('/game');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    const fd = new FormData(e.target);
    try {
      await register(fd.get('email') || '', fd.get('username'), fd.get('password'));
      navigate('/game');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#FDFDFD] relative overflow-hidden">
      {/* Geometric decorations */}
      <div className="geo-decoration top-12 left-12">
        <Square size={120} strokeWidth={0.5} className="text-zinc-900" />
      </div>
      <div className="geo-decoration bottom-20 right-16">
        <Triangle size={160} strokeWidth={0.5} className="text-zinc-900" />
      </div>
      <div className="geo-decoration top-1/3 right-1/4">
        <Circle size={80} strokeWidth={0.5} className="text-zinc-900" />
      </div>

      <div className="w-full max-w-md z-10">
        <div className="text-center mb-10">
          <h1 className="text-5xl md:text-7xl font-bold tracking-tighter leading-none mb-3" data-testid="auth-title">
            Open<br />Structures
          </h1>
          <p className="text-base md:text-lg text-zinc-500 leading-relaxed">
            Build within the field of possibilities
          </p>
        </div>

        <Card className="border border-zinc-200 shadow-none rounded-sm" data-testid="auth-card">
          <CardHeader className="pb-4">
            <CardTitle className="text-xl font-semibold tracking-tight">Enter the Field</CardTitle>
            <CardDescription>Sign in or create an account to begin building</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="login" className="w-full">
              <TabsList className="w-full mb-6">
                <TabsTrigger value="login" className="flex-1" data-testid="login-tab">Sign In</TabsTrigger>
                <TabsTrigger value="register" className="flex-1" data-testid="register-tab">Register</TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-username">Username</Label>
                    <Input id="login-username" name="username" type="text" required placeholder="Your username" data-testid="login-username" className="rounded-sm" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password">Password</Label>
                    <Input id="login-password" name="password" type="password" required placeholder="Enter password" data-testid="login-password" className="rounded-sm" />
                  </div>
                  <Button type="submit" disabled={loading} className="w-full bg-zinc-900 text-white hover:bg-zinc-800 rounded-sm h-11 font-medium" data-testid="login-submit">
                    {loading ? 'Signing in...' : 'Sign In'}
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="reg-username">Username</Label>
                    <Input id="reg-username" name="username" type="text" required placeholder="Choose a name" data-testid="register-username" className="rounded-sm" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reg-password">Password</Label>
                    <Input id="reg-password" name="password" type="password" required placeholder="Create password" data-testid="register-password" className="rounded-sm" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reg-email" className="text-zinc-500">Email <span className="text-xs text-zinc-400">(optional – for updates & musings)</span></Label>
                    <Input id="reg-email" name="email" type="email" placeholder="you@example.com" data-testid="register-email" className="rounded-sm" />
                  </div>
                  <Button type="submit" disabled={loading} className="w-full bg-zinc-900 text-white hover:bg-zinc-800 rounded-sm h-11 font-medium" data-testid="register-submit">
                    {loading ? 'Creating account...' : 'Create Account'}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
        
        {/* About Button */}
        <button
          onClick={() => setShowAbout(true)}
          className="mt-6 w-full flex items-center justify-center gap-2 text-zinc-400 hover:text-zinc-600 transition-colors text-sm font-mono"
          data-testid="about-btn"
        >
          <Info size={14} />
          About Open Structures
        </button>
      </div>
      
      {/* About Dialog */}
      <Dialog open={showAbout} onOpenChange={setShowAbout}>
        <DialogContent className="max-w-2xl max-h-[80vh] rounded-sm">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Open Structures</DialogTitle>
            <DialogDescription className="text-zinc-500">
              A meditation on becoming
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-[60vh] pr-4">
            <div className="prose prose-zinc prose-sm max-w-none">
              {ABOUT_TEXT.split('\n\n').map((paragraph, i) => (
                <p key={i} className="text-zinc-600 leading-relaxed mb-4 whitespace-pre-wrap">
                  {paragraph}
                </p>
              ))}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}
